import { ElementRef, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
// eslint-disable-next-line no-duplicate-imports
import { VideoQuality, type VideoPlayer } from '@zoom/videosdk';
import { Radio } from 'antd';
import classnames from 'classnames';
import type React from 'react';
import { useHistory, useLocation, useParams, type RouteComponentProps } from 'react-router-dom';
import ZoomMediaContext from '../../context/media-context';
import ZoomContext from '../../context/zoom-context';
import { usePrevious } from '../../hooks';
import type { Participant } from '../../index-types';
import Avatar from './components/avatar';
import Pagination from './components/pagination';
import ShareView from './components/share-view';
import VideoFooter from './components/video-footer';
import AvatarActionContext from './context/avatar-context';
import { usePagination } from './hooks/useAttachPagination';
import { useAvatarAction } from './hooks/useAvatarAction';
import { useActiveVideo } from './hooks/useAvtiveVideo';
import { useNetworkQuality } from './hooks/useNetworkQuality';
import { useParticipantsChange } from './hooks/useParticipantsChange';
import { useVideoAspect } from './hooks/useVideoAspectRatio';
// import Draggable from 'react-draggable';
import { Box, Flex } from '@chakra-ui/react';
import { UrlPramsPreview } from '../../App';
import useLanguageSelector from '../../hooks/useLanguageSelector';
import useLesson from '../../hooks/useLesson';
import useLessonContext from '../../hooks/useLessonContext';
import useLogger from '../../hooks/useLogger';
import { useUser } from '../../hooks/useUser';
import useUserContext from '../../hooks/useUserContext';
import { JoinService } from '../../service/join-service';
import { isMobile } from '../../utils/DeviceType';
import { enterPictureInPicture } from '../../utils/pip-utils';
import ChatContainer from '../chat/chat';
import RemoteCameraControlPanel from './components/remote-camera-control';
import { useChatView } from './hooks/useChatView';
import { useSpotlightVideo } from './hooks/useSpotlightVideo';
import classNames from 'classnames';
import { SendLoggerService } from '../../service/send-logger-service';

interface ExtendedParticipant extends Participant {
  spotlighted?: boolean;
}

type VideoAttachProps = RouteComponentProps;

const VideoContainer: React.FunctionComponent<VideoAttachProps> = (props) => {
  const { mediaStream } = useContext(ZoomMediaContext);
  const LANG = useLanguageSelector();

  // const preferPageCount = mediaStream?.getMaxRenderableVideos() ?? 1;
  const preferPageCount = 4;
  const zmClient = useContext(ZoomContext);
  const { page, pageSize, totalPage, setPage } = usePagination(zmClient, preferPageCount);
  const shareViewRef = useRef<{ selfShareRef: HTMLCanvasElement | HTMLVideoElement | null }>(null);

  const videoPlayerListRef = useRef<Record<string, VideoPlayer>>({});
  const [isRecieveSharing, setIsRecieveSharing] = useState(false);
  const [spotlightUsers, setSpotlightUsers] = useState<number[]>([]);
  const [participants, setParticipants] = useState<ExtendedParticipant[]>([]);
  const [currentPageParticipants, setCurrentPageParticipants] = useState<Participant[]>([]);
  const [currentUser, setCurrentUser] = useState<Participant>(zmClient.getCurrentUserInfo());
  const [subscribers, setSubscribers] = useState<number[]>([]);
  // video-active-change イベントのみで管理するビデオ有効ユーザーセット
  // user-updated 経由の bVideoOn は race condition があるため使わない
  const [videoActiveUserIds, setVideoActiveUserIds] = useState<Set<number>>(
    () => new Set(zmClient.getAllUser().filter((u) => u.bVideoOn).map((u) => u.userId))
  );
  const activeVideo = useActiveVideo(zmClient);
  const avatarActionState = useAvatarAction(zmClient, participants, true);
  const networkQuality = useNetworkQuality(zmClient);
  const previousSubscribers = usePrevious(subscribers);
  const aspectRatio = useVideoAspect(zmClient);
  const optionsOfVideoResolution = [
    { label: '1080P', value: VideoQuality.Video_1080P },
    { label: '720P', value: VideoQuality.Video_720P },
    { label: '360P', value: VideoQuality.Video_360P },
    { label: '180P', value: VideoQuality.Video_180P },
    { label: '90P', value: VideoQuality.Video_90P }
  ];
  const { isOpenChat, togleChatView } = useChatView();
  const history = useHistory();
  const lessonCtx = useLessonContext();
  const userCtx = useUserContext();
  const joinService = useMemo(() => new JoinService(), []);
  const roomId = useParams<UrlPramsPreview>().roomId;
  const companyId = useParams<UrlPramsPreview>().companyId;
  const { lessonInfo } = useLesson(companyId, roomId);
  const { userInfo } = useUser();
  const logger = useLogger();
  const [isStartedVideo, setIsStartedVideo] = useState(zmClient?.getCurrentUserInfo()?.bVideoOn);
  const currentPage = useLocation().pathname;
  const sendLoggerService = useMemo(() => new SendLoggerService(), []);

  const handleChatButton = () => {
    sendLoggerService.sendLog({
      // isOpenChatは押す前の状態
      action: isOpenChat ? 'チャットボタン押下(Close)' : 'チャットボタン押下(Open)',
      lessonId: lessonCtx?.lessonId ?? '',
      roomId: lessonCtx?.roomId ?? '',
      userId: `${zmClient.getSessionInfo().userId}`,
      page: currentPage,
      advancedInfo: {}
    });

    togleChatView();
  };

  useEffect(() => {
    const onVideoActiveChange = ({ state, userId }: { state: 'Active' | 'Inactive'; userId: number }) => {
      setVideoActiveUserIds((prev) => {
        const next = new Set(prev);
        if (state === 'Active') next.add(userId);
        else next.delete(userId);
        return next;
      });
    };
    // peer-video-state-change は peer のビデオ開始/停止をより確実に捕捉する
    const onPeerVideoStateChange = ({ action, userId }: { action: 'Start' | 'Stop'; userId: number }) => {
      setVideoActiveUserIds((prev) => {
        const next = new Set(prev);
        if (action === 'Start') next.add(userId);
        else next.delete(userId);
        return next;
      });
    };
    zmClient.on('video-active-change', onVideoActiveChange);
    zmClient.on('peer-video-state-change', onPeerVideoStateChange);
    return () => {
      zmClient.off('video-active-change', onVideoActiveChange);
      zmClient.off('peer-video-state-change', onPeerVideoStateChange);
    };
  }, [zmClient]);

  // マウント直後は getAllUser() が空の場合があるため、少し遅らせて再取得する
  useEffect(() => {
    const timer = setTimeout(() => {
      setVideoActiveUserIds((prev) => {
        const next = new Set(prev);
        zmClient.getAllUser().filter((u) => u.bVideoOn).forEach((u) => next.add(u.userId));
        return next;
      });
    }, 1500);
    return () => clearTimeout(timer);
  }, [zmClient]);

  useSpotlightVideo(zmClient, mediaStream, (p) => {
    setSpotlightUsers(p.map((user) => user.userId));
  });

  useParticipantsChange(zmClient, (allParticipants, updatedParticipants) => {
    let participants: ExtendedParticipant[] = [];
    let tempCurUserInfo: Participant | null = null;
    // enhance failover logic
    if (!currentUser?.userId) {
      tempCurUserInfo = zmClient.getCurrentUserInfo();
      setCurrentUser(tempCurUserInfo);
    }
    updatedParticipants?.forEach((p) => {
      if (p?.userId === currentUser?.userId && Object.keys(p).length > 1) {
        setCurrentUser((c) => ({
          ...c,
          ...p
        }));
      }
    });

    if (allParticipants.length > 0) {
      participants = allParticipants
        .filter((p) => p.userId !== (currentUser?.userId || tempCurUserInfo?.userId))
        .sort((user1, user2) => Number(user2.bVideoOn) - Number(user1.bVideoOn));
    }
    setParticipants(participants);

    // allParticipants の bVideoOn=true を videoActiveUserIds に追加（削除はしない）
    // 削除は video-active-change(Inactive) のみが行う（race condition 防止）
    setVideoActiveUserIds((prev) => {
      const next = new Set(prev);
      allParticipants.filter((u) => u.bVideoOn).forEach((u) => next.add(u.userId));
      return next;
    });
  });

  useEffect(() => {
    let currPageParticipants: ExtendedParticipant[] = [...participants, currentUser]
      .map((p) => ({ ...p, spotlighted: spotlightUsers.includes(p?.userId) }))
      .sort((user1, user2) => Number(user2.spotlighted) - Number(user1.spotlighted))
      .slice(page * pageSize, page * pageSize + pageSize);
    setCurrentPageParticipants(currPageParticipants);
    // bVideoOn = true OR videoActiveUserIds に含まれる場合に subscribe
    // videoActiveUserIds は race condition で誤って false になった bVideoOn に対するガード
    setSubscribers(
      currPageParticipants
        .filter((user) => user.bVideoOn || videoActiveUserIds.has(user.userId))
        .map((u) => u.userId)
    );
  }, [pageSize, page, participants, spotlightUsers, videoActiveUserIds]);

  const setVideoPlayerRef = (userId: number, element: VideoPlayer | null) => {
    if (element) {
      videoPlayerListRef.current[`${userId}`] = element;
      element.setAttribute('data-user-id', String(userId));
    } else {
      delete videoPlayerListRef.current[`${userId}`];
    }
  };
  useEffect(() => {
    const addedUsers = subscribers.filter((user) => !(previousSubscribers || []).includes(user));
    const removedUsers = (previousSubscribers || []).filter((user) => !subscribers.includes(user));
    if (removedUsers.length > 0) {
      removedUsers.forEach((userId) => {
        mediaStream?.detachVideo(userId);
      });
    }
    if (addedUsers.length > 0) {
      addedUsers.forEach((userId) => {
        const attachment = videoPlayerListRef.current[`${userId}`];
        if (attachment) {
          mediaStream?.attachVideo(userId, VideoQuality.Video_720P, attachment);
        }
      });
    }
  }, [subscribers, previousSubscribers, mediaStream]);
  // FIXME currentユーザーも他ユーザーと一緒のリストに入れているためこっちの処理は不要
  // useEffect(() => {
  //   if (currentUser?.bVideoOn) {
  //     const attachment = videoPlayerListRef.current[`${currentUser?.userId}`];
  //     if (attachment) {
  //       mediaStream?.attachVideo(currentUser?.userId, VideoQuality.Video_720P, attachment);
  //     }
  //   } else {
  //     try {
  //       mediaStream?.detachVideo(currentUser?.userId).catch((error) => {
  //         logger.error(error);
  //       });
  //     } catch (error) {
  //       logger.error(error);
  //     }
  //   }
  // }, [currentUser, mediaStream]);

  const onVideoResolutionChange = useCallback(
    ({ target: { value } }: any, userId: number) => {
      const attachment = videoPlayerListRef.current[`${userId}`];
      attachment;
      mediaStream?.attachVideo(userId, value, attachment);
    },
    [videoPlayerListRef, mediaStream]
  );
  const gridColumns = useMemo(() => {
    if (isRecieveSharing) return 1;
    if (spotlightUsers.length) {
      return Math.sqrt(pageSize) * 2;
    }

    return Math.sqrt(pageSize);
  }, [spotlightUsers, isRecieveSharing, pageSize]);

  const gridRows = useMemo(() => {
    if (isRecieveSharing) {
      return pageSize;
    }
    if (spotlightUsers.length) {
      return Math.sqrt(pageSize) * 2;
    }

    const rows = Math.ceil(currentPageParticipants.length / Math.sqrt(pageSize));
    // console.log(rows, currentPageParticipants.length, pageSize);
    return rows;
    // return Math.sqrt(pageSize);
  }, [spotlightUsers, isRecieveSharing, pageSize, currentPageParticipants]);
  const gridColumnsMobile = useMemo(() => {
    if (isRecieveSharing) return 1;
    if (spotlightUsers.length) {
      return Math.sqrt(pageSize) * 2;
    }
    if (currentPageParticipants.length <= pageSize / 2) {
      return 1;
    }

    return Math.sqrt(pageSize);
  }, [spotlightUsers, isRecieveSharing, pageSize, currentPageParticipants]);
  const gridRowsMobile = useMemo(() => {
    if (isRecieveSharing) {
      return pageSize;
    }
    if (spotlightUsers.length) {
      return Math.sqrt(pageSize) * 2;
    }
    if (currentPageParticipants.length === 2) {
      return 2;
    }
    const rows = Math.ceil(currentPageParticipants.length / Math.sqrt(pageSize));
    console.log(rows, currentPageParticipants.length, pageSize);
    return rows;
    // return Math.sqrt(pageSize);
  }, [spotlightUsers, isRecieveSharing, pageSize, currentPageParticipants]);

  useEffect(() => {
    // セッション開始前にここに来た場合
    const session = zmClient.getSessionInfo();
    if (session.isInMeeting) {
      return;
    }
    if (!userInfo) {
      return;
    }
    if (!lessonInfo) {
      return;
    }
    joinService.joinRoom(userInfo, lessonInfo, zmClient);
  }, [userInfo, lessonInfo, zmClient]);

  const videoContainerRef = useRef<ElementRef<'div'>>(null);

  const startPiPView = useCallback(() => {
    if (!videoContainerRef.current) return;
    const activePlayers = Object.fromEntries(
      subscribers
        .filter((id) => videoPlayerListRef.current[`${id}`])
        .map((id) => [`${id}`, videoPlayerListRef.current[`${id}`]])
    );
    enterPictureInPicture(videoContainerRef.current, LANG.KT_0100_33, mediaStream, activePlayers);
  }, [mediaStream, subscribers]);
  return isMobile() ? (
    <>
      <Flex flexDirection={'column'}>
        <Box className="viewport">
          <ShareView ref={shareViewRef} onRecieveSharingChange={setIsRecieveSharing} />
          <Box
            className={classnames('video-container', 'video-container-attech', {
              'video-container-in-sharing-mobile': isRecieveSharing
            })}
            ref={videoContainerRef}
            display={isRecieveSharing ? 'none' : ''}
          >
            <video-player-container class="video-container-wrap">
              <AvatarActionContext.Provider value={avatarActionState}>
                <ul
                  className="user-list"
                  style={{
                    gridTemplateColumns: `repeat(${gridColumnsMobile}, minmax(128px, 1fr))`,
                    gridTemplateRows: `repeat(${gridRowsMobile},minmax(72px, 1fr))`
                  }}
                >
                  {currentPageParticipants.map((user) => {
                    return (
                      <div
                        className={classnames('video-cell', { 'video-cell-spotlight': (user as any).spotlighted })}
                        key={user.userId + '-video-cell'}
                      >
                        {avatarActionState?.avatarActionState[user?.userId]?.videoResolutionAdjust?.toggled && (
                          <div className="change-video-resolution">
                            <Radio.Group
                              options={optionsOfVideoResolution}
                              onChange={(value) => {
                                onVideoResolutionChange(value, user.userId);
                              }}
                              defaultValue={VideoQuality.Video_720P}
                              optionType="button"
                              buttonStyle="solid"
                            />
                          </div>
                        )}
                        <div
                          className="aspact-ratio"
                          style={aspectRatio[`${user.userId}`] ? { aspectRatio: aspectRatio[`${user.userId}`] } : {}}
                        >
                          {currentUser?.userId === user?.userId ? (
                            <>
                              {(currentUser?.bVideoOn || videoActiveUserIds.has(currentUser?.userId)) && (
                                <div>
                                  <video-player
                                    class="video-player"
                                    ref={(element) => {
                                      setVideoPlayerRef(currentUser?.userId, element);
                                    }}
                                  />
                                </div>
                              )}
                              {currentUser && (
                                <Avatar
                                  participant={currentUser as Participant}
                                  key={currentUser?.userId as number}
                                  isActive={false}
                                  networkQuality={networkQuality[`${currentUser?.userId}`]}
                                />
                              )}
                            </>
                          ) : (
                            <>
                              {(user.bVideoOn || videoActiveUserIds.has(user.userId)) && (
                                <video-player
                                  class="video-player"
                                  ref={(element) => {
                                    setVideoPlayerRef(user.userId, element);
                                  }}
                                />
                              )}

                              <Avatar
                                participant={user}
                                key={user.userId}
                                isActive={activeVideo === user.userId}
                                networkQuality={networkQuality[`${user.userId}`]}
                              />
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {
                    // <div
                    //   className={classnames('video-cell')}
                    //   style={{
                    //     width: isAndroidOrIOSBrowser() ? '50vw' : '30vw'
                    //   }}
                    // >
                    //   <AvatarActionContext.Provider value={avatarActionState}>
                    //     {currentUser?.bVideoOn && (
                    //       <div>
                    //         <video-player
                    //           class="video-player"
                    //           ref={(element) => {
                    //             setVideoPlayerRef(currentUser?.userId, element);
                    //           }}
                    //         />
                    //       </div>
                    //     )}
                    //     {currentUser && (
                    //       <Avatar
                    //         participant={currentUser as Participant}
                    //         key={currentUser?.userId as number}
                    //         isActive={false}
                    //         networkQuality={networkQuality[`${currentUser?.userId}`]}
                    //       />
                    //     )}
                    //   </AvatarActionContext.Provider>
                    // </div>
                  }
                </ul>
                {zmClient.getSessionInfo()?.isInMeeting && <RemoteCameraControlPanel />}
              </AvatarActionContext.Provider>
            </video-player-container>
          </Box>

          <VideoFooter
            className={classnames('video-operations', { 'open-chat': isOpenChat })}
            sharing
            selfShareCanvas={shareViewRef.current?.selfShareRef}
            togleChatView={handleChatButton}
            startPiPView={startPiPView}
          />

          {/* FIXME 共有中は */}
          {(totalPage > 1 && !isRecieveSharing) ||
            (totalPage > 1 && !isMobile() && isRecieveSharing && (
              <Pagination page={page} totalPage={totalPage} setPage={setPage} inSharing={isRecieveSharing} />
            ))}
          {/* 今のところ不要 */}
          {/* <ReportBtn /> */}
        </Box>
        {isOpenChat && <ChatContainer onCloseChatView={handleChatButton} />}
      </Flex>
    </>
  ) : (
    <>
      <Flex>
        <Box className="viewport" height={isOpenChat && isMobile() ? '50vh' : ''}>
          <ShareView ref={shareViewRef} onRecieveSharingChange={setIsRecieveSharing} />
          {/* <div
            className="unified-self-view"
            style={{
              width: isAndroidOrIOSBrowser() ? '50vw' : '30vw'
            }}
          >
            <Draggable
              className="unified-self-view"
              customstyle={{
                width: isAndroidOrIOSBrowser() ? '50vw' : '30vw'
              }}
            >
              <video-player-container class="unified-self-view-container">
                <AvatarActionContext.Provider value={avatarActionState}>
                  {currentUser?.bVideoOn && (
                    <div>
                      <video-player
                        class="video-player"
                        ref={(element) => {
                          setVideoPlayerRef(currentUser?.userId, element);
                        }}
                      />
                    </div>
                  )}
                  {currentUser && (
                    <Avatar
                      participant={currentUser as Participant}
                      key={currentUser?.userId as number}
                      isActive={false}
                      networkQuality={networkQuality[`${currentUser?.userId}`]}
                    />
                  )}
                </AvatarActionContext.Provider>
              </video-player-container>
            </Draggable>
          </div> */}

          <Box
            className={classnames('video-container', 'video-container-attech', {
              'video-container-in-sharing': isRecieveSharing
            })}
            ref={videoContainerRef}
          >
            <video-player-container class="video-container-wrap">
              <AvatarActionContext.Provider value={avatarActionState}>
                <ul
                  className="user-list"
                  style={{
                    gridTemplateColumns: `repeat(${gridColumns}, minmax(128px, 1fr))`,
                    gridTemplateRows: `repeat(${gridRows},minmax(72px, 1fr))`
                  }}
                >
                  {currentPageParticipants.map((user) => {
                    return (
                      <div
                        className={classnames('video-cell', { 'video-cell-spotlight': (user as any).spotlighted })}
                        key={user.userId + '-video-cell'}
                      >
                        {avatarActionState?.avatarActionState[user?.userId]?.videoResolutionAdjust?.toggled && (
                          <div className="change-video-resolution">
                            <Radio.Group
                              options={optionsOfVideoResolution}
                              onChange={(value) => {
                                onVideoResolutionChange(value, user.userId);
                              }}
                              defaultValue={VideoQuality.Video_720P}
                              optionType="button"
                              buttonStyle="solid"
                            />
                          </div>
                        )}
                        <div
                          className="aspact-ratio"
                          style={aspectRatio[`${user.userId}`] ? { aspectRatio: aspectRatio[`${user.userId}`] } : {}}
                        >
                          {(user.bVideoOn || videoActiveUserIds.has(user.userId)) && (
                            <video-player
                              class="video-player"
                              ref={(element) => {
                                setVideoPlayerRef(user.userId, element);
                              }}
                            />
                          )}
                          <Avatar
                            participant={user}
                            key={user.userId}
                            isActive={activeVideo === user.userId}
                            networkQuality={networkQuality[`${user.userId}`]}
                          />
                        </div>
                      </div>
                    );
                  })}
                  {
                    // <div
                    //   className={classnames('video-cell')}
                    //   style={{
                    //     width: isAndroidOrIOSBrowser() ? '50vw' : '30vw'
                    //   }}
                    // >
                    //   <AvatarActionContext.Provider value={avatarActionState}>
                    //     {currentUser?.bVideoOn && (
                    //       <div>
                    //         <video-player
                    //           class="video-player"
                    //           ref={(element) => {
                    //             setVideoPlayerRef(currentUser?.userId, element);
                    //           }}
                    //         />
                    //       </div>
                    //     )}
                    //     {currentUser && (
                    //       <Avatar
                    //         participant={currentUser as Participant}
                    //         key={currentUser?.userId as number}
                    //         isActive={false}
                    //         networkQuality={networkQuality[`${currentUser?.userId}`]}
                    //       />
                    //     )}
                    //   </AvatarActionContext.Provider>
                    // </div>
                  }
                </ul>
                {zmClient.getSessionInfo()?.isInMeeting && <RemoteCameraControlPanel />}
              </AvatarActionContext.Provider>
            </video-player-container>
          </Box>
          <VideoFooter
            className="video-operations"
            sharing
            selfShareCanvas={shareViewRef.current?.selfShareRef}
            togleChatView={handleChatButton}
            startPiPView={startPiPView}
            participants={participants}
          />

          {/* FIXME 共有中は */}
          {(totalPage > 1 && !isRecieveSharing) ||
            (totalPage > 1 && !isMobile() && isRecieveSharing && (
              <Pagination page={page} totalPage={totalPage} setPage={setPage} inSharing={isRecieveSharing} />
            ))}
          {/* 今のところ不要 */}
          {/* <ReportBtn /> */}
        </Box>
        {isOpenChat && <ChatContainer onCloseChatView={handleChatButton} />}
      </Flex>
    </>
  );
};
export default VideoContainer;
